// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { useMemo, useRef, useState } from "react";
import {
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "@/components/theme-provider";
import { ResizableCard } from "@/components/ui/resizable-card";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import type { Activity } from "@/hooks/api/use-account-activities";
import { usePersonActivityTimeline } from "@/hooks/api/use-person-activity-timeline";
import { useOrganizationSessionStorage } from "@/hooks/use-browser-storage";
import { ChartHeaders } from "@/pages/accounts/account-explorer/minimap/chart-headers.tsx";
import { ActivityTooltip } from "@/pages/accounts/account-explorer/minimap/chart-tooltips.tsx";
import FilteredActivityDots from "@/pages/accounts/account-explorer/minimap/filtered-activity-dots.tsx";
import { useChartData } from "@/pages/accounts/account-explorer/minimap/hooks.ts";
import { OpportunityChart } from "@/pages/accounts/account-explorer/minimap/opportunity-chart.tsx";
import { getZeroValuePeriodHeight } from "@/pages/accounts/account-explorer/minimap/utils.ts";

/**
 * Threshold for showing filtered activity dots
 */
const FILTERED_ACTIVITIES_THRESHOLD_RATIO = 0.9;

interface PersonActivityChartsProps {
  personId?: string;
  onDateClick?: (date: string) => void;
  visibleRange?: { start: string; end: string };
  filteredActivities?: Activity[];
  customTimeRange: { start: Date; end: Date } | null;
  setCustomTimeRange: (range: { start: Date; end: Date } | null) => void;
  opportunities?: import("@/hooks/api/use-account-opportunities").Opportunity[];
}

interface ChartMouseEvent {
  activeLabel?: string | number;
  activePayload?: Array<{
    payload?: {
      id?: string;
      timestamp?: number;
    };
  }>;
  chartX?: number;
  chartY?: number;
}

export function PersonActivityCharts({
  personId,
  onDateClick,
  visibleRange,
  filteredActivities,
  customTimeRange,
  setCustomTimeRange,
  opportunities,
}: PersonActivityChartsProps) {
  const { theme } = useTheme();
  const { state: sidebarState, isMobile } = useSidebar();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectionStart, setSelectionStart] = useState<number | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<number | null>(null);
  const [showOutgoingLine, setShowOutgoingLine] = useState(false);

  // Store and retrieve chart height from organization-scoped sessionStorage
  const [chartHeight, setChartHeight] = useOrganizationSessionStorage<number>(
    "persons::explorer::minimap::height",
    200, // Smaller default since we have fewer charts
  );

  // Define fixed elements and proportions
  const HANDLE_HEIGHT = 8;
  const MIN_HEADER_HEIGHT = 30;
  const MAX_HEADER_HEIGHT = 50;
  const X_AXIS_HEIGHT = 30; // Height reserved for the date labels in the activity chart
  const CHART_GAPS = 4; // Gap between charts

  // Calculate header height proportionally to total height
  const headerHeight = useMemo(() => {
    const targetHeight = Math.floor(chartHeight * 0.12);
    return Math.max(
      MIN_HEADER_HEIGHT,
      Math.min(MAX_HEADER_HEIGHT, targetHeight),
    );
  }, [chartHeight]);

  // Calculate the actual chart area height (excluding fixed elements)
  const chartAreaHeight = useMemo(
    () =>
      chartHeight - HANDLE_HEIGHT - headerHeight - X_AXIS_HEIGHT - CHART_GAPS,
    [chartHeight, headerHeight],
  );

  // Get timeline data
  const { data: timelineData, isLoading: isLoadingTimeline } =
    usePersonActivityTimeline(personId);

  // Calculate opportunity chart dynamic height based on the number of opportunities
  const dynamicOpportunityHeight = useMemo(() => {
    if (!opportunities?.length) return 10;

    const uniqueOpportunityIds = new Set(opportunities.map((o) => o.id));
    const count = uniqueOpportunityIds.size;
    const heightPerOpportunity = 6;

    return Math.max(20, Math.min(count * heightPerOpportunity, 100));
  }, [opportunities]);

  // Calculate proportions - just activity and opportunity charts
  const proportions = useMemo(() => {
    const opportunityProportion = dynamicOpportunityHeight / chartAreaHeight;
    const activityProportion = Math.max(0, 1 - opportunityProportion);

    return {
      activity: activityProportion,
      opportunity: opportunityProportion,
    };
  }, [chartAreaHeight, dynamicOpportunityHeight]);

  // Calculate individual chart heights
  const calculateHeights = useMemo(() => {
    const rawHeights = {
      activity: chartAreaHeight * proportions.activity,
      opportunity: chartAreaHeight * proportions.opportunity,
    };

    const roundedHeights = {
      activity: Math.floor(rawHeights.activity),
      opportunity: Math.floor(rawHeights.opportunity),
    };

    const totalRounded = roundedHeights.activity + roundedHeights.opportunity;
    const remaining = chartAreaHeight - totalRounded;

    // Distribute remaining pixels
    if (remaining > 0) {
      const fractions = {
        activity: rawHeights.activity - roundedHeights.activity,
        opportunity: rawHeights.opportunity - roundedHeights.opportunity,
      };

      if (fractions.activity >= fractions.opportunity) {
        roundedHeights.activity += remaining;
      } else {
        roundedHeights.opportunity += remaining;
      }
    }

    return roundedHeights;
  }, [chartAreaHeight, proportions]);

  const activityChartHeight = calculateHeights.activity;
  const opportunityChartHeight = calculateHeights.opportunity;

  // Use the custom hook to get chart data - pass undefined for getPersonById since not needed
  const {
    visibleTimeRange,
    chartWidth,
    activityChartData,
    zeroValuePeriods,
    domain,
    ticks,
    tickFormatter,
  } = useChartData(
    timelineData,
    opportunities,
    undefined, // no first touchpoint data
    customTimeRange,
    undefined, // no person lookup needed
  );

  // Add drag selection handlers
  const handleMouseDown = (e: ChartMouseEvent) => {
    if (!e?.activeLabel) return;
    setIsDragging(true);
    setSelectionStart(
      typeof e.activeLabel === "string"
        ? parseInt(e.activeLabel, 10)
        : e.activeLabel,
    );
    setSelectionEnd(
      typeof e.activeLabel === "string"
        ? parseInt(e.activeLabel, 10)
        : e.activeLabel,
    );
  };

  const handleMouseMove = (e: ChartMouseEvent) => {
    if (!isDragging || !e?.activeLabel) return;
    setSelectionEnd(
      typeof e.activeLabel === "string"
        ? parseInt(e.activeLabel, 10)
        : e.activeLabel,
    );
  };

  const handleMouseUp = () => {
    if (!isDragging || !selectionStart || !selectionEnd) {
      if (!isDragging && selectionStart) {
        onDateClick?.(new Date(selectionStart).toISOString());
      }
      setIsDragging(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      return;
    }

    const start = Math.min(selectionStart, selectionEnd);
    const end = Math.max(selectionStart, selectionEnd);

    // If selection is too small, treat as a click
    if (end - start < 7 * 24 * 60 * 60 * 1000) {
      onDateClick?.(new Date(start).toISOString());
      setIsDragging(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      return;
    }

    setCustomTimeRange({
      start: new Date(start),
      end: new Date(end),
    });

    setIsDragging(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    onDateClick?.(new Date(start).toISOString());
  };

  // Calculate max width based on viewport and sidebar state
  const maxWidth = isMobile
    ? "calc(100vw - 2rem)"
    : sidebarState === "collapsed"
      ? "calc(100vw - var(--sidebar-width-icon) - 2rem)"
      : "calc(100vw - var(--sidebar-width) - 2rem)";

  if (isLoadingTimeline) {
    return (
      <div className="relative w-full" style={{ maxWidth }}>
        <Skeleton className="w-full" style={{ height: `${chartHeight}px` }} />
      </div>
    );
  }

  return (
    <div className="relative w-full" style={{ maxWidth }}>
      <ResizableCard
        defaultHeight={chartHeight}
        minHeight={120}
        maxHeight={500}
        onHeightChange={setChartHeight}
        roundedBottom={false}
        className="overflow-visible"
      >
        <ChartHeaders
          showOutgoingLine={showOutgoingLine}
          setShowOutgoingLine={setShowOutgoingLine}
          customTimeRange={customTimeRange}
          setCustomTimeRange={setCustomTimeRange}
          headerHeight={headerHeight}
        />

        <div
          ref={scrollContainerRef}
          className={`relative w-full px-6 ${
            customTimeRange ? "overflow-x-auto" : ""
          }`}
        >
          <div style={{ width: chartWidth }}>
            <div
              className="relative overflow-visible"
              style={{ height: `${activityChartHeight}px` }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={activityChartData}
                  margin={{ top: 0, right: 20, bottom: -30, left: 25 }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  <XAxis
                    dataKey="timestamp"
                    scale="time"
                    type="number"
                    domain={domain}
                    ticks={ticks}
                    interval={0}
                    axisLine={false}
                    tickLine={false}
                    tick={false}
                    height={30}
                  />
                  <YAxis hide domain={[-2, 10]} />
                  <Tooltip
                    content={<ActivityTooltip />}
                    wrapperStyle={{ position: "absolute", zIndex: 50 }}
                    cursor={{ stroke: "hsl(var(--chart-5))", strokeWidth: 1 }}
                    position={{ y: 0 }}
                    isAnimationActive={false}
                  />

                  <ReferenceLine
                    y={0}
                    stroke="hsl(var(--border))"
                    strokeWidth={1}
                    pointerEvents="none"
                  />

                  <Line
                    type="monotone"
                    dataKey="decayed_in_value"
                    stroke="hsl(var(--chart-5))"
                    strokeWidth={1.5}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                  {showOutgoingLine && (
                    <Line
                      type="monotone"
                      dataKey="decayed_out_value"
                      stroke="hsl(var(--muted-foreground) / 0.5)"
                      strokeWidth={1.5}
                      dot={false}
                      connectNulls
                      isAnimationActive={false}
                      name="decayed_out_value"
                    />
                  )}

                  {zeroValuePeriods.map((period, index) => {
                    const height = getZeroValuePeriodHeight(
                      period,
                      customTimeRange,
                    );
                    return (
                      <ReferenceArea
                        key={`zero-period-${index}`}
                        x1={period.start}
                        x2={period.end}
                        y1={-height}
                        y2={height}
                        fill={
                          theme === "dark"
                            ? "hsl(var(--accent))"
                            : "hsl(var(--accent))"
                        }
                        fillOpacity={1}
                        stroke="transparent"
                        pointerEvents="none"
                      />
                    );
                  })}
                  {visibleRange && (
                    <>
                      <ReferenceArea
                        x1={Math.max(
                          domain[0],
                          new Date(visibleRange.start).getTime(),
                        )}
                        x2={Math.min(
                          domain[1],
                          new Date(visibleRange.end).getTime(),
                        )}
                        fill="transparent"
                        stroke="transparent"
                        label={({ viewBox }) => {
                          const { x, y, width } = viewBox;
                          return (
                            <line
                              x1={x}
                              y1={y}
                              x2={x + width}
                              y2={y}
                              stroke="hsl(var(--chart-5))"
                              strokeWidth={1}
                              pointerEvents="none"
                            />
                          );
                        }}
                        className="transition-all duration-200 ease-out"
                        pointerEvents="none"
                      />
                      <ReferenceLine
                        x={Math.max(
                          domain[0],
                          new Date(visibleRange.start).getTime(),
                        )}
                        stroke="hsl(var(--chart-5))"
                        strokeWidth={0.5}
                        className="transition-all duration-200 ease-out"
                        pointerEvents="none"
                      />
                      <ReferenceLine
                        x={Math.min(
                          domain[1],
                          new Date(visibleRange.end).getTime(),
                        )}
                        stroke="hsl(var(--chart-5))"
                        strokeWidth={0.5}
                        className="transition-all duration-200 ease-out"
                        pointerEvents="none"
                      />
                    </>
                  )}

                  {isDragging && selectionStart && selectionEnd && (
                    <ReferenceArea
                      x1={Math.min(selectionStart, selectionEnd)}
                      x2={Math.max(selectionStart, selectionEnd)}
                      fill="hsl(var(--chart-5))"
                      fillOpacity={0.2}
                      stroke="hsl(var(--chart-5))"
                      strokeOpacity={0.5}
                      pointerEvents="none"
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>

              {filteredActivities &&
                timelineData?.activities &&
                filteredActivities.length > 0 &&
                filteredActivities.length <
                  timelineData.activities.length *
                    FILTERED_ACTIVITIES_THRESHOLD_RATIO && (
                  <div className="absolute left-[25px] right-[20px] bottom-0 h-4">
                    <FilteredActivityDots
                      data={filteredActivities}
                      domain={domain as [number, number]}
                      height={8}
                      onDateClick={onDateClick}
                    />
                  </div>
                )}
            </div>

            <div
              style={{ height: `${opportunityChartHeight + X_AXIS_HEIGHT}px` }}
            >
              <OpportunityChart
                accountId={undefined} // Not needed for person explorer
                domain={domain as [number, number]}
                ticks={ticks}
                visibleRange={visibleRange}
                visibleTimeRange={visibleTimeRange}
                isLoading={false} // Loading is handled at the parent level
                onDateClick={onDateClick}
                opportunitiesOverride={opportunities}
                showDateLabels
                tickFormatter={tickFormatter}
              />
            </div>
          </div>
        </div>
      </ResizableCard>
    </div>
  );
}
